import { useTheme } from '@emotion/react';
import { Box } from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import Highstock from 'highcharts/highstock';
import { DateTime } from 'luxon';
import { useEffect, useRef } from 'react';
import { darkTheme, lightTheme } from '../../../components/highcharts/themes';
import { formatNumber } from '@/utils/formatters';

const PortfolioAnalysisDataChart = ({ data = [], isLoading, unit = 'kWh' }) => {
  const chartComponentRef = useRef(null);
  const theme = useTheme();
  const seriesData = data?.Data?.map((item) => [
    new Date(item.timestamp).getTime(),
    item.value,
  ]);

  const options = {
    chart: {
      zoomType: 'x',
    },
    title: {
      text: ``,
    },
    xAxis: {
      type: 'datetime',
      labels: {
        formatter: function () {
          const millis = DateTime.fromMillis(this.value);
          if (this.dateTimeLabelFormat.includes('e') || this.isFirst) {
            return millis.setLocale('da-DK').toLocaleString({
              month: 'short',
              day: 'numeric',
              timeZone: 'Europe/Copenhagen',
            });
          } else if (this.dateTimeLabelFormat.includes('y') || this.isFirst) {
            return millis.setLocale('da-DK').toLocaleString({
              month: 'short',
              year: '2-digit',
              timeZone: 'Europe/Copenhagen',
            });
          } else
            return millis.setLocale('da-DK').toLocaleString({
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'Europe/Copenhagen',
            });
        },
      },
    },
    yAxis: {
      labels: {
        format: `{text} ${unit}`,
      },
      title: {
        text: '',
      },
    },
    series: [
      {
        name: 'Forbrug',
        data: seriesData,
      },
    ],
    legend: {
      enabled: false,
    },
    tooltip: {
      formatter: function () {
        const millis = DateTime.fromMillis(this.x);
        const date = millis.setLocale('da-DK').toLocaleString({
          month: 'short',
          day: 'numeric',
          year: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'UTC',
        });
        return `
                          <span>${date}</span><br/>
                          <span style="color:${
                            this.point.color
                          }">\u25CF</span> Forbrug: ${formatNumber(
                            this.y,
                          )} ${unit}<br/>
                      `;
      },
    },
  };
  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;

    if (chartComponentRef.current?.chart) {
      const chart = chartComponentRef.current.chart;
      chart.update(
        {
          chart: { backgroundColor: themeOptions.chart.backgroundColor },
          time: {
            // timezoneOffset: new Date().getTimezoneOffset('da-DK'),
            useUTC: true,
          },
          title: { style: { color: themeOptions.title.style.color } },
          xAxis: {
            crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },

            gridLineColor: 'transparent',
            lineColor: themeOptions.xAxis.lineColor,
            labels: { style: { color: themeOptions.xAxis.labels.style.color } },
          },
          yAxis: [
            {
              crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
              gridLineColor: 'transparent',
              labels: {
                style: { color: themeOptions.yAxis.labels.style.color },
              },
            },
          ],
          legend: {
            itemStyle: { color: themeOptions.legend.itemStyle.color },
          },
          tooltip: {
            backgroundColor: themeOptions.tooltip.backgroundColor,
            borderColor: themeOptions.tooltip.borderColor,
            style: {
              color: theme.palette.mode === 'dark' ? 'white' : 'black', // ✅ Dynamic text color
            },
          },
        },
        false,
      );
      chart.redraw();
    }
  }, [theme]);
  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (chart) {
      if (isLoading) {
        chart.showLoading('Loading data...');
      } else {
        chart.hideLoading();
      }
    }
  }, [isLoading]);
  return (
    <Box width={'100%'} sx={{ mt: 2 }}>
      <HighchartsReact
        highcharts={Highstock}
        options={options}
        ref={chartComponentRef}
      />
    </Box>
  );
};

export default PortfolioAnalysisDataChart;
